import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SkeletonCard } from '@/components/ui/SkeletonLoader';
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { alunoService, cursoService, cidadeService } from '@/services/api';
import { AlunoFilters, Cidade, Curso, PaginationResponse, Aluno } from '@/types';
import { PlusIcon, PencilIcon, TrashIcon, UserGroupIcon, EyeIcon } from '@heroicons/react/24/outline';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';

export const AlunosPage: React.FC = () => {
  const [filters, setFilters] = useState<AlunoFilters>({
    page: 1,
    limit: 10,
  });
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateAlunos } = useQueryInvalidation();
  const { dialogState, showConfirm, closeDialog } = useConfirmDialog();

  const [searchText, setSearchText] = useState<string>("");
  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1, search: searchText || undefined }));
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const [loadError, setLoadError] = useState<string | null>(null);

  const { data: alunosData, isLoading, error } = useQuery<PaginationResponse<Aluno>>({
    queryKey: ['alunos', filters],
    queryFn: () => alunoService.list(filters),
  });

  useEffect(() => {
    if (error) {
      const message = handleError(error as any, 'Erro ao carregar alunos');
      setLoadError(message);
    }
  }, [error, handleError]);

  useEffect(() => {
    if (alunosData) {
      setLoadError(null);
    }
  }, [alunosData]);

  const deleteAlunoMutation = useMutation({
    mutationFn: alunoService.delete,
    onSuccess: (_data, deletedId) => {
      invalidateAlunos(deletedId);
      handleSuccess('Aluno excluído com sucesso!');
    },
    onError: (error: Error) => {
      handleError(error, 'Erro ao excluir aluno');
    },
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleDelete = async (id: number) => {
    showConfirm(
      'Excluir Aluno',
      'Tem certeza que deseja excluir este aluno? Esta ação não pode ser desfeita.',
      async () => {
        await deleteAlunoMutation.mutateAsync(id);
      },
      'danger'
    );
  };


  if (loadError) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:justify-between sm:items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Alunos</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerencie os alunos cadastrados no sistema
          </p>
        </div>
        <Link to="/alunos/novo" className="w-full sm:w-auto">
          <Button className="w-full sm:w-auto">
            <PlusIcon className="h-4 w-4 mr-2" />
            Novo Aluno
          </Button>
        </Link>
      </div>

      {/* Stats */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center">
            <UserGroupIcon className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total de Alunos</p>
              <p className="text-2xl font-bold text-gray-900">{alunosData?.pagination?.total || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Alunos */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle>Lista de Alunos</CardTitle>
              <CardDescription>
                Todos os alunos cadastrados no sistema
              </CardDescription>
            </div>
            <div className="w-full flex flex-col gap-3">
              <div className="w-full">
                <input
                  type="text"
                  placeholder="Buscar por nome ou CPF"
                  className="w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <ServerSearchSelect<Curso>
                  value={filters.cursoId ?? null}
                  onChange={(selectedId) => setFilters((prev) => ({ ...prev, page: 1, cursoId: selectedId ?? undefined }))}
                  fetchPage={(params) => cursoService.list(params)}
                  fetchById={(selectedId) => cursoService.getById(selectedId)}
                  getOptionLabel={(curso) => curso.nome}
                  queryKey="cursos-alunos-filter"
                  placeholder="Todos os cursos"
                />
                <ServerSearchSelect<Cidade>
                  value={filters.cidadeId ?? null}
                  onChange={(selectedId) => setFilters((prev) => ({ ...prev, page: 1, cidadeId: selectedId ?? undefined }))}
                  fetchPage={(params) => cidadeService.list(params)}
                  fetchById={(selectedId) => cidadeService.getById(selectedId)}
                  getOptionLabel={(cidade) => `${cidade.nome} — ${cidade.uf}`}
                  queryKey="cidades-alunos-filter"
                  placeholder="Todas as cidades"
                />
              </div>
              {(filters.search || filters.cursoId || filters.cidadeId) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSearchText(""); setFilters({ page: 1, limit: filters.limit }); }}
                  className="w-full sm:w-auto"
                >
                  Limpar Filtros
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonCard count={5} />
          ) : alunosData?.data?.length === 0 ? (
            <div className="text-center py-8">
              <UserGroupIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum aluno encontrado</h3>
              <p className="text-gray-500 mb-4">Comece criando um novo aluno.</p>
              <Link to="/alunos/novo">
                <Button asChild>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Novo Aluno
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {alunosData?.data?.map((aluno) => (
                <div
                  key={aluno.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      <UserGroupIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                          {aluno.nome}
                        </h3>
                      </div>
                    </div>

                    <div className="flex space-x-2 justify-end sm:justify-start">
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-initial text-blue-600 hover:text-blue-700 hover:border-blue-300"
                        title="Ver detalhes"
                      >
                        <Link to={`/alunos/${aluno.id}`} className="flex items-center justify-center gap-1">
                          <EyeIcon className="h-4 w-4" />
                          <span className="sm:hidden">Ver</span>
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex-1 sm:flex-initial text-yellow-600 hover:text-yellow-700 hover:border-yellow-300"
                        title="Editar"
                      >
                        <Link to={`/alunos/${aluno.id}/editar`} className="flex items-center justify-center gap-1">
                          <PencilIcon className="h-4 w-4" />
                          <span className="sm:hidden">Editar</span>
                        </Link>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(aluno.id)}
                        disabled={deleteAlunoMutation.isPending}
                        className="text-red-600 hover:text-red-700 hover:border-red-300 flex-1 sm:flex-initial"
                        title="Excluir"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <TrashIcon className="h-4 w-4" />
                          <span className="sm:hidden">Excluir</span>
                        </div>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Paginação */}
          {alunosData && alunosData.pagination?.totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) - 1)}
                  disabled={(filters.page || 1) <= 1}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-3 py-2 text-sm text-gray-700">
                  Página {filters.page || 1} de {alunosData.pagination?.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange((filters.page || 1) + 1)}
                  disabled={(filters.page || 1) >= (alunosData.pagination?.totalPages || 1)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        onConfirm={dialogState.onConfirm}
        title={dialogState.title}
        message={dialogState.message}
        type={dialogState.type}
        confirmText="Excluir"
        cancelText="Cancelar"
        isLoading={deleteAlunoMutation.isPending}
      />
    </div>
  );
};